import { APP_EVENTS, ONBOARDING_EVENTS } from "@/constants/analyticsEvents";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api } from "~/convex/_generated/api";
import { useAccount } from "@/state/useAccount";
import { useConvexMutation } from "@convex-dev/react-query";
import { useForm } from "@tanstack/react-form";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { zodValidator } from "@tanstack/zod-form-adapter";

const appSchema = z.object({
  name: z
    .string()
    .min(1, "App name is required")
    .max(50, "App name is too long"),
  description: z
    .string()
    .min(1, "Please describe your app")
    .max(500, "Description is too long"),
  website: z
    .string()
    .refine(
      (val) => val === "" || z.string().url().safeParse(val).success,
      "Please enter a valid website URL",
    )
    .optional(),
  iosAppStoreLink: z
    .string()
    .refine(
      (val) => val === "" || z.string().url().safeParse(val).success,
      "Please enter a valid App Store URL",
    )
    .optional(),
  androidAppStoreLink: z
    .string()
    .refine(
      (val) => val === "" || z.string().url().safeParse(val).success,
      "Please enter a valid Play Store URL",
    )
    .optional(),
});

export const Route = createFileRoute(
  "/_app/_auth/onboarding/_layout/app_onboarding",
)({
  component: AppOnboarding,
  beforeLoad: () => ({
    title: `App Details`,
  }),
});

export default function AppOnboarding() {
  const { mutateAsync: createApp, isPending } = useMutation({
    mutationFn: useConvexMutation(api.apps.createApp),
    onError: (error) => {
      console.error("Failed to create app:", error);
    },
  });
  const navigate = useNavigate();
  const { setAppId } = useAccount();
  const { track } = useMixpanel();

  const form = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      name: "",
      description: "",
      website: "",
      iosAppStoreLink: "",
      androidAppStoreLink: "",
    },
    validators: {
      onSubmit: appSchema,
    },
    onSubmit: async ({ value }) => {
      console.log("Form submitted with values:", value);
      const app = await createApp(value);
      if (app) {
        setAppId(app);

        // Track app creation
        track(APP_EVENTS.APP_CREATED, {
          app_id: app,
          app_name: value.name,
          has_website: !!value.website,
          has_ios_link: !!value.iosAppStoreLink,
          has_android_link: !!value.androidAppStoreLink,
          timestamp: new Date().toISOString(),
        });

        track(ONBOARDING_EVENTS.ONBOARDING_COMPLETED, {
          app_id: app,
          user_type: "appFounder",
          timestamp: new Date().toISOString(),
        });
      } else {
        console.error("Failed to create app");
        return;
      }

      console.log("App created successfully");
      navigate({ to: DashboardRoute.fullPath });
    },
  });

  return (
    <div className="mx-auto flex min-h-full w-full max-w-xl flex-col justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <h3 className="text-center text-xl sm:text-2xl font-medium text-primary">
          Tell Us About Your App
        </h3>
        <p className="text-center text-sm sm:text-base font-normal text-primary/60">
          Help creators understand what makes your app special
        </p>
      </div>

      <form
        className="flex w-full flex-col items-start gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            await form.handleSubmit();
          } catch (error) {
            console.error("Form submission error:", error);
          }
        }}
      >
        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="name" className="text-sm font-medium text-primary">
              App Name
            </label>
            <form.Field
              name="name"
              children={(field) => (
                <Input
                  placeholder="My Amazing App"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    "border-destructive focus-visible:ring-destructive"
                  }`}
                />
              )}
            />
            {form.state.fieldMeta.name?.errors.length > 0 && (
              <span className="text-sm text-destructive">
                {form.state.fieldMeta.name?.errors.join(" ")}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="description"
              className="text-sm font-medium text-primary"
            >
              Description
            </label>
            <form.Field
              name="description"
              children={(field) => (
                <Textarea
                  placeholder="Describe your app in a few sentences"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`min-h-[100px] bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    "border-destructive focus-visible:ring-destructive"
                  }`}
                />
              )}
            />
            {form.state.fieldMeta.description?.errors.length > 0 && (
              <span className="text-sm text-destructive">
                {form.state.fieldMeta.description?.errors.join(" ")}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="website"
              className="flex items-center gap-2 text-sm font-medium text-primary"
            >
              Website
              <span className="text-xs font-normal text-primary/40">
                (optional)
              </span>
            </label>
            <form.Field
              name="website"
              children={(field) => (
                <Input
                  placeholder="https://example.com"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    "border-destructive focus-visible:ring-destructive"
                  }`}
                />
              )}
            />
            {form.state.fieldMeta.website?.errors.length > 0 && (
              <span className="text-sm text-destructive">
                {form.state.fieldMeta.website?.errors.join(" ")}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="iosAppStoreLink"
              className="flex items-center gap-2 text-sm font-medium text-primary"
            >
              App Store Link
              <span className="text-xs font-normal text-primary/40">
                (optional)
              </span>
            </label>
            <form.Field
              name="iosAppStoreLink"
              children={(field) => (
                <Input
                  placeholder="https://apps.apple.com/app/..."
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    "border-destructive focus-visible:ring-destructive"
                  }`}
                />
              )}
            />
            {form.state.fieldMeta.iosAppStoreLink?.errors.length > 0 && (
              <span className="text-sm text-destructive">
                {form.state.fieldMeta.iosAppStoreLink?.errors.join(" ")}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="androidAppStoreLink"
              className="flex items-center gap-2 text-sm font-medium text-primary"
            >
              Play Store Link
              <span className="text-xs font-normal text-primary/40">
                (optional)
              </span>
            </label>
            <form.Field
              name="androidAppStoreLink"
              children={(field) => (
                <Input
                  placeholder="https://play.google.com/store/apps/..."
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    "border-destructive focus-visible:ring-destructive"
                  }`}
                />
              )}
            />
            {form.state.fieldMeta.androidAppStoreLink?.errors.length > 0 && (
              <span className="text-sm text-destructive">
                {form.state.fieldMeta.androidAppStoreLink?.errors.join(" ")}
              </span>
            )}
          </div>
        </div>

        <Button type="submit" size="sm" className="w-full" disabled={isPending}>
          {isPending ? <Loader2 className="animate-spin" /> : "Create App"}
        </Button>
      </form>

      <form.Subscribe
        selector={(state) => ({
          canSubmit: state.canSubmit,
          isSubmitting: state.isSubmitting,
          errors: state.errors,
        })}
      >
        {(state) => {
          if (state.errors) {
            return (
              <div className="mt-2 text-sm text-destructive">
                {Object.values(state.errors).join(", ")}
              </div>
            );
          }
          return null;
        }}
      </form.Subscribe>

      <p className="px-6 text-center text-xs sm:text-sm font-normal leading-normal text-primary/60">
        You can update your app details at any time from your app settings.
      </p>
    </div>
  );
}
